// client/src/coms/components/PackBrowser.js
// Browse published sticker packs (GET /stickers/discover): search by name, creator or keyword, sort, filter
// free / paid, paged. Opening a pack shows the pack information page with the actions the server allows
// (add to my stickers, buy, enable for a server).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { comsApiGet } from '../comsApi';
import PackInfoDialog, { PackIcon, formatPrice } from './PackInfoDialog';
import UserAvatar from '../views/games_parts/UserAvatar';

function errText(e, fallback) {
  return String((e && e.data && e.data.error) || (e && e.message) || fallback);
}

const field = { background: 'var(--vxs-1, #111214)', color: 'var(--vx-text, #fff)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 10, padding: '10px 12px', fontSize: 14 };

export const SORTS = [
  { key: 'newest', label: 'Newest first' },
  { key: 'popular', label: 'Most popular' },
  { key: 'name', label: 'Name (A–Z)' },
  { key: 'price_low', label: 'Price: low to high' },
  { key: 'price_high', label: 'Price: high to low' },
];

// serverKey: open packs in "enable for this server" mode (server settings). onChanged: after install/enable.
export default function PackBrowser({ serverKey = '', onChanged, pageSize = 24, filters = true }) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('newest');
  const [price, setPrice] = useState('');
  const [packs, setPacks] = useState([]);
  const [next, setNext] = useState(null); // { cursor } or { offset }
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(null);
  const seq = useRef(0);

  const fetchPage = useCallback(
    async (q, page) => {
      const mine = ++seq.current;
      setLoading(true);
      setErr('');
      try {
        const params = new URLSearchParams({ limit: String(pageSize), sort });
        if (q) params.set('q', q);
        if (price) params.set('price', price);
        if (page && page.cursor) params.set('cursor', String(page.cursor));
        if (page && page.offset) params.set('offset', String(page.offset));
        const r = await comsApiGet(`/stickers/discover?${params.toString()}`);
        if (mine !== seq.current) return; // a newer search replaced this one
        setPacks((cur) => (page ? [...cur, ...(r.packs || [])] : r.packs || []));
        setNext(r.next_cursor ? { cursor: r.next_cursor } : r.next_offset ? { offset: r.next_offset } : null);
      } catch (e) {
        if (mine === seq.current) setErr(errText(e, 'Could not load packs.'));
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    },
    [pageSize, price, sort]
  );

  // Search as you type, debounced (one request per pause, not per keystroke).
  useEffect(() => {
    const t = setTimeout(() => fetchPage(query.trim(), null), 300);
    return () => clearTimeout(t);
  }, [query, fetchPage]);

  async function openPack(p) {
    setErr('');
    try {
      const r = await comsApiGet(`/stickers/packs/${p.id}`);
      setOpen(r.pack);
    } catch (e) {
      setErr(errText(e, 'Could not open the pack.'));
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search packs by name, creator or keyword"
          aria-label="Search sticker packs"
          style={{ ...field, flex: '1 1 240px', boxSizing: 'border-box' }}
        />
        {filters ? (
          <>
            <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort packs" style={field}>
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <select value={price} onChange={(e) => setPrice(e.target.value)} aria-label="Price" style={field}>
              <option value="">Free and paid</option>
              <option value="free">Free</option>
              <option value="paid">Paid</option>
            </select>
          </>
        ) : null}
      </div>
      {err ? <div className="coms-form-error" role="alert" style={{ marginTop: 8 }}>{err}</div> : null}
      <div className="vox-pack-grid" role="list" aria-label="Sticker packs" aria-busy={loading}>
        {packs.map((p) => (
          <button key={p.id} type="button" role="listitem" className="vox-pack-tile" onClick={() => openPack(p)} aria-label={`${p.name} by ${p.creator || 'someone'}, ${p.sticker_count} stickers, ${p.is_free ? 'free' : formatPrice(p.price)}`}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%' }}>
              <PackIcon pack={p} size={40} />
              <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                <div style={{ fontSize: 11, opacity: 0.75, display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                  <UserAvatar userId={p.owner_user_id} name={p.creator || '?'} size={14} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.creator || 'someone'} · {p.sticker_count} stickers{p.install_count ? ` · 👥 ${p.install_count}` : ''}</span>
                </div>
              </div>
              <span className={`vox-pack-badge ${p.is_free ? 'free' : 'paid'}`}>{p.is_free ? 'Free' : formatPrice(p.price)}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, width: '100%', marginTop: 8 }}>
              {(p.stickers || []).slice(0, 4).map((s) => (
                <img key={s.id} src={s.url} alt="" loading="lazy" style={{ width: '100%', aspectRatio: '1', objectFit: 'contain', background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.03 * var(--vx-ov, 1)))', borderRadius: 6 }} />
              ))}
            </div>
            {p.description ? <div style={{ fontSize: 11, opacity: 0.65, marginTop: 6, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{p.description}</div> : null}
            {p.installed || p.is_mine || p.access === 'purchased' ? <div style={{ fontSize: 11, marginTop: 6, color: '#a7f3d0' }}>{p.is_mine ? 'Your pack' : p.access === 'purchased' ? 'Bought' : 'In your stickers'}</div> : null}
          </button>
        ))}
        {loading && !packs.length ? Array.from({ length: 3 }, (_, i) => <div key={`s${i}`} className="vox-pack-tile vox-pack-skeleton" aria-hidden="true" />) : null}
      </div>
      {!loading && !packs.length && !err ? <div className="vox-sticker-empty" style={{ marginTop: 10 }}>{query.trim() || price ? 'No packs match your search.' : 'No packs have been published yet. Publish one from Settings → Stickers → My packs.'}</div> : null}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
        {next ? (
          <button type="button" className="coms-mini-btn" disabled={loading} onClick={() => fetchPage(query.trim(), next)}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        ) : loading && packs.length ? (
          <span style={{ fontSize: 12, opacity: 0.7 }}>Loading…</span>
        ) : null}
      </div>
      {open ? (
        <PackInfoDialog
          pack={open}
          serverKey={serverKey}
          onClose={() => setOpen(null)}
          onChanged={(kind) => {
            if (kind === 'install') setPacks((cur) => cur.map((x) => (x.id === open.id ? { ...x, installed: true } : x)));
            if (typeof onChanged === 'function') onChanged(kind);
          }}
        />
      ) : null}
    </div>
  );
}
